import { Columns3CogIcon } from 'lucide-react'

import copy from 'copy-to-clipboard'
import { getDefaultStore } from 'jotai'
import { proxy } from 'valtio'

import { useStoreKeySelector } from '@/lib/valtio/store-hooks'

import type { ColumnPatch } from '~shared/rundown/column'

import { planAtom } from '@/store/plan.store'
import { toastAtom } from '@/store/toast.store'

import { getColumn } from '../data/columns.store'
import { modalActions } from '../modals/modal.store'
import { rundownPageStore } from '../rundown-page.store'

import { readColumnAccess } from './column-access'
import { columnLayoutActions } from './column-layout'
import { getStepTargetSlot, moveColumnToVisibleSlot } from './column-reorder'
import {
  addColumn,
  clearPrivateColumn,
  deleteColumn,
  reorderColumns,
  reorderPrivateColumn,
  updateColumn,
  updatePrivateColumn,
} from './column.api'
import { COLUMN_TYPE_DESCRIPTIONS, COLUMN_TYPE_LABELS } from './column.const'
import type { ColumnConversionTarget } from './column.const'
import { columnsDerived } from './column.derived'

type ColumnActionsState = {
  /** An add request is in flight. */
  isAddingColumn: boolean
}

/** Private: only `columnActions.add` writes it. */
const columnActionsStore = proxy<ColumnActionsState>({ isAddingColumn: false })

/**
 * Every mutation a column or its header can trigger.
 *
 * @remarks
 *   Requests toast their own errors.
 */
export const columnActions = {
  /**
   * Patch the column document, eg. its name, type or width.
   *
   * The user's own private column goes through the private route, which only
   * needs a READ token, so a viewer can resize their private notes.
   *
   * Rejects on failure, so callers like `InlineEdit` can revert.
   */
  update: async (columnId: string, payload: ColumnPatch) => {
    // A column whose document is missing counts as public.
    const { isOwnPrivate } = readColumnAccess({
      id: columnId,
      privateUid: getColumn(columnId)?.privateUid ?? null,
    })
    const params = { rundownId: rundownPageStore.rundown.id, columnId, payload }

    if (isOwnPrivate) {
      await updatePrivateColumn(params)
    } else {
      await updateColumn(params)
    }
  },

  /**
   * Convert the column to another type, after a confirmation dialog.
   *
   * The UI deliberately treats this as destructive, even though cell content
   * is currently only hidden.
   */
  convert: async (columnId: string, type: ColumnConversionTarget) => {
    const column = getColumn(columnId)
    if (!column || column.type === type) return

    const name = column.name.trim() || 'Unnamed'
    const to = COLUMN_TYPE_LABELS[type]
    const explanation = COLUMN_TYPE_DESCRIPTIONS[type]

    const didConfirm = await modalActions.open('ALERT_DIALOG', {
      title: `Convert "${name}" column to ${to}?`,
      description: `${explanation}\n\nAll content in this column will be lost.`,
      variant: 'destructive',
      icon: Columns3CogIcon,
      confirmLabel: `Convert column`,
    })
    if (!didConfirm) return

    // Failures already toast
    await columnActions.update(columnId, { type }).catch(() => {})
  },

  /**
   * Remove the column, after a confirmation dialog.
   */
  delete: async (columnId: string) => {
    const column = getColumn(columnId)
    if (!column) return

    const { isOwnPrivate: isPrivateNotes } = readColumnAccess(column)
    const name = column.name.trim() || 'Unnamed'
    const didConfirm = await modalActions.open('ALERT_DIALOG', {
      title: isPrivateNotes
        ? `Clear "${name}" column?`
        : `Delete "${name}" column?`,
      description: isPrivateNotes
        ? 'This permanently removes all data in it.'
        : 'The column will be moved to the trash.',
      variant: 'destructive',
      confirmLabel: isPrivateNotes ? 'Clear' : 'Delete',
    })
    if (!didConfirm) return

    const params = { rundownId: rundownPageStore.rundown.id, columnId }

    try {
      if (isPrivateNotes) {
        await clearPrivateColumn(params)
      } else {
        await deleteColumn(params)
      }
    } catch {
      // The request already toasted
    }
  },

  /**
   * Add a column right after `afterColumnId`, or after the last visible column.
   *
   * The one entry point for adding a column, so every menu that offers it
   * respects the plan's column limit. Past the limit it shows the upgrade modal
   * instead. Ignored while another add is in flight.
   *
   * @remarks
   *   Without `afterColumnId` the column goes after the last visible one, not
   *   at the end of the rundown's order. With a custom layout that is the end
   *   of the grid the user sees. With no visible column it is appended.
   */
  add: async (afterColumnId?: string) => {
    if (columnActionsStore.isAddingColumn) return

    if (isColumnsLimitExceeded()) {
      rundownPageStore.modals.upgradeModal?.show({
        message: "You've reached your limit for columns on this rundown.",
      })
      return
    }

    const after = afterColumnId ?? columnsDerived.visibleColumns.at(-1)?.id

    columnActionsStore.isAddingColumn = true
    try {
      await addColumn({
        rundownId: rundownPageStore.rundown.id,
        params: after ? { after } : undefined,
      })
    } catch {
      // The request already toasted.
    } finally {
      columnActionsStore.isAddingColumn = false
    }
  },

  /**
   * Copy the column id to the clipboard and toast.
   */
  copyId: (columnId: string) => {
    copy(columnId)
    jotai.set(toastAtom, {
      title: 'Column ID copied to the clipboard',
      body: `Column ID: ${columnId}`,
      type: 'success',
    })
  },

  /**
   * Move a column to the drop slot `visibleSlot` among the visible columns.
   */
  moveToVisibleIndex: (columnId: string, visibleSlot: number) => {
    // The whole order is written back, so it must include ids this user
    // cannot see.
    const { visibleColumns, layoutColumnIds } = columnsDerived
    const moved = moveColumnToVisibleSlot(
      visibleColumns,
      layoutColumnIds,
      columnId,
      visibleSlot,
    )
    if (!moved) return

    columnLayoutActions.setOrder(moved.order)

    const rundownId = rundownPageStore.rundown.id
    // A column whose document is missing counts as public.
    const access = readColumnAccess({
      id: columnId,
      privateUid: getColumn(columnId)?.privateUid ?? null,
    })

    // The requests already toast their errors.
    if (access.isOwnPrivate) {
      const afterColumnId =
        moved.newIndex > 0 ? moved.order[moved.newIndex - 1] : null
      reorderPrivateColumn({
        rundownId,
        columnId,
        payload: { afterColumnId },
      }).catch(() => {})
    } else if (access.canWriteColumn) {
      reorderColumns({ rundownId, columnIds: moved.order }).catch(() => {})
    }
  },

  /**
   * Move a column left or right by one visible slot.
   */
  moveByDirection: (columnId: string, direction: 'left' | 'right') => {
    const slot = getStepTargetSlot(
      columnsDerived.visibleColumns,
      columnId,
      direction,
    )
    if (slot === null) return

    columnActions.moveToVisibleIndex(columnId, slot)
  },
}

// =============== HELPERS

const jotai = getDefaultStore()

/**
 * Whether adding another column would exceed the plan's column limit.
 *
 * Counts ids, not loaded columns, like the server (`rundown.columns.length`).
 * That includes private columns and ids whose document is missing.
 */
function isColumnsLimitExceeded(): boolean {
  const plan = jotai.get(planAtom)
  if (!plan) return false

  return (
    plan.limits.columns !== -1 &&
    plan.limits.columns <= columnsDerived.layoutColumnIds.length
  )
}

// =============== HOOKS

/** Is an add request in flight? For disabling the add buttons. */
export function useIsAddingColumn(): boolean {
  return useStoreKeySelector(
    columnActionsStore,
    'isAddingColumn',
    (isAddingColumn) => isAddingColumn,
  )
}
